import React, { useState } from "react";
import { Flag, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Textarea } from "../ui/textarea";
import { cn } from "../../lib/utils";
import { toast } from "../../lib/toast";
import { ApiHttpError } from "../../lib/api-client";
import {
  SIGNALEMENT_CATEGORIES, createSignalement, currentPagePath,
  type SignalementCategory, type CreatedSignalement,
} from "../../lib/signalements";
import { useSignalementSubject } from "./signalement-subject";

const MESSAGE_MAX_LENGTH = 1000;

/**
 * The red flag in the header: a small light-red icon, no banner and no call to
 * action — reporting is something a user reaches for when something is wrong,
 * not something the app asks everyone to do. What it reports is the screen the
 * user is on, plus whatever that screen declared (see signalement-subject.tsx).
 * The dialog itself is owned by the app shell, so the menu entry opens the same one.
 */
export function SignalementFlagButton({ onClick, className }: { onClick: () => void; className?: string }) {
  const { t } = useTranslation("common");

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={onClick}
      aria-label={t("signalement.action")}
      title={t("signalement.action")}
      className={cn("shrink-0 self-center bg-destructive-soft text-destructive transition-all hover:bg-destructive/20 hover:text-destructive [&_svg]:transition-all", className)}
    >
      <Flag className="h-[18px] w-[18px]" aria-hidden />
    </Button>
  );
}

/** Mount only while open, so each report starts from a clean form. */
export function SignalementDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation("common");
  const subject = useSignalementSubject();
  const [category, setCategory] = useState<SignalementCategory | null>(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pageUrl = currentPagePath();

  const submit = async () => {
    if (category === null || sending) return;
    setSending(true);
    setError(null);

    try {
      const created: CreatedSignalement = await createSignalement({ category, message, subject, pageUrl });
      toast.success(created.duplicate ? t("signalement.dejaTransmis") : t("signalement.merci"));
      onClose();
    } catch (failure) {
      // Nothing entered is lost: the dialog stays open with the text and the choice.
      setError(failure instanceof ApiHttpError ? failure.message : t("signalement.echec"));
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={(next) => { if (!next && !sending) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-4 w-4 text-destructive" aria-hidden />
            {t("signalement.titre")}
          </DialogTitle>
          <DialogDescription>{t("signalement.intro")}</DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-border bg-muted/40 px-3 py-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {subject ? t(`signalement.sujet.${subject.type}`) : t("signalement.sujetEcran")}
          </p>
          <p className="truncate text-sm font-medium" title={subject?.label ?? pageUrl}>
            {subject?.label ?? pageUrl}
          </p>
          {subject?.label && <p className="truncate text-xs text-muted-foreground">{pageUrl}</p>}
        </div>

        <fieldset className="grid gap-2">
          <legend className="mb-1 text-sm font-medium">{t("signalement.quoi")}</legend>
          <RadioGroup
            value={category ?? ""}
            onValueChange={(value) => setCategory(value as SignalementCategory)}
            className="gap-1.5"
          >
            {SIGNALEMENT_CATEGORIES.map((value) => (
              <label
                key={value}
                htmlFor={`signalement-${value}`}
                onClick={() => setCategory(value)}
                className={cn(
                  "flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2 transition-colors",
                  category === value ? "border-primary bg-secondary/60" : "border-border hover:bg-muted/60",
                )}
              >
                <RadioGroupItem value={value} id={`signalement-${value}`} className="mt-0.5" />
                <span className="min-w-0">
                  <span className="block text-sm font-medium">{t(`signalement.categorie.${value}.label`)}</span>
                  <span className="block text-xs text-muted-foreground">{t(`signalement.categorie.${value}.aide`)}</span>
                </span>
              </label>
            ))}
          </RadioGroup>
        </fieldset>

        <div className="grid gap-1.5">
          <Label htmlFor="signalement-message">{t("signalement.message")}</Label>
          <Textarea
            id="signalement-message"
            value={message}
            onChange={(event) => setMessage(event.target.value.slice(0, MESSAGE_MAX_LENGTH))}
            placeholder={t("signalement.messagePlaceholder")}
            rows={3}
            maxLength={MESSAGE_MAX_LENGTH}
          />
          <p className="text-right text-xs text-muted-foreground">
            {t("signalement.compteur", { count: message.length, max: MESSAGE_MAX_LENGTH })}
          </p>
        </div>

        {error !== null && (
          <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={sending}>
            {t("signalement.annuler")}
          </Button>
          <Button onClick={submit} disabled={category === null || sending}>
            {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
            {t("signalement.envoyer")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
